import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { FaCheck, FaTimes, FaEye, FaBox, FaTools, FaClock, FaMapMarkerAlt, FaUser, FaEnvelope } from 'react-icons/fa';
import AdminLayout from '../../components/AdminLayout';

export default function RequestApprovals() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [requestToReject, setRequestToReject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchPendingRequests();
    }
  }, [user]);

  const fetchPendingRequests = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/requests/admin/pending?admin_id=${user.id}`);
      setPendingRequests(response.data);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors du chargement' : 'Error loading requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await axios.post(`${API}/requests/admin/approve/${requestId}?admin_id=${user.id}`);
      toast.success(i18n.language === 'fr' ? 'Demande approuvée avec succès' : 'Request approved successfully');
      fetchPendingRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors de l\'approbation' : 'Error approving request');
    }
  };

  const handleReject = async () => {
    if (!requestToReject) return;
    
    try {
      const params = new URLSearchParams({ admin_id: user.id });
      if (rejectReason) {
        params.append('reason', rejectReason);
      }
      
      await axios.post(`${API}/requests/admin/reject/${requestToReject.id}?${params.toString()}`);
      toast.success(i18n.language === 'fr' ? 'Demande rejetée' : 'Request rejected');
      setRejectDialogOpen(false);
      setRejectReason('');
      setRequestToReject(null);
      fetchPendingRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors du rejet' : 'Error rejecting request');
    }
  };

  const openRejectDialog = (request) => {
    setRequestToReject(request);
    setRejectDialogOpen(true);
  };

  const viewRequest = (request) => {
    setSelectedRequest(request);
    setDialogOpen(true);
  };

  const getTypeIcon = (type) => {
    return type === 'product' ? <FaBox /> : <FaTools />;
  };

  const getTypeLabel = (type) => {
    return type === 'product' 
      ? (i18n.language === 'fr' ? 'Produit' : 'Product')
      : (i18n.language === 'fr' ? 'Service' : 'Service');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0A2540] mb-2">
            {i18n.language === 'fr' ? 'Approbation des Demandes' : 'Request Approvals'}
          </h1>
          <p className="text-gray-600">
            {i18n.language === 'fr'
              ? 'Approuver ou rejeter les demandes avant publication'
              : 'Approve or reject requests before publication'}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {i18n.language === 'fr' ? 'Chargement...' : 'Loading...'}
            </p>
          </div>
        ) : pendingRequests.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">
              {i18n.language === 'fr'
                ? 'Aucune demande en attente d\'approbation'
                : 'No requests pending approval'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pendingRequests.map((request, index) => (
              <Card key={request.id} data-testid={`request-${index}`}>
                <CardContent className="p-6">
                  {/* Type Badge */}
                  <div className="flex items-center gap-2 mb-3">
                    {getTypeIcon(request.request_type)}
                    <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                      {i18n.language === 'fr' ? 'En attente' : 'Pending'}
                    </Badge>
                  </div>

                  {/* Title */}
                  <h3 className="font-bold text-xl mb-2 text-[#0A2540]">
                    {i18n.language === 'fr' ? request.title_fr : request.title_en}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                    {i18n.language === 'fr' ? request.description_fr : request.description_en}
                  </p>

                  {/* Details */}
                  <div className="space-y-2 mb-4 text-sm">
                    <div className="flex items-center gap-2 text-gray-700">
                      <FaUser className="text-gray-400" />
                      <span>{request.user_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <FaEnvelope className="text-gray-400" />
                      <span className="text-xs">{request.user_email}</span>
                    </div>
                    {request.location && (
                      <div className="flex items-center gap-2 text-gray-700">
                        <FaMapMarkerAlt className="text-gray-400" />
                        <span>{request.location}</span>
                      </div>
                    )}
                    {request.budget && (
                      <div className="text-gray-700">
                        <strong>{i18n.language === 'fr' ? 'Budget:' : 'Budget:'}</strong> {request.budget} {request.currency?.toUpperCase() || 'EUR'}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-gray-500 text-xs">
                      <FaClock />
                      <span>{formatDate(request.created_at)}</span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => viewRequest(request)}
                      className="flex-1"
                      data-testid={`view-request-${index}`}
                    >
                      <FaEye className="mr-1" /> {i18n.language === 'fr' ? 'Voir' : 'View'}
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleApprove(request.id)}
                      className="flex-1 bg-[#635BFF] hover:bg-[#5248d9]"
                      data-testid={`approve-request-${index}`}
                    >
                      <FaCheck className="mr-1" /> {i18n.language === 'fr' ? 'Approuver' : 'Approve'}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => openRejectDialog(request)}
                      className="flex-1"
                      data-testid={`reject-request-${index}`}
                    >
                      <FaTimes className="mr-1" /> {i18n.language === 'fr' ? 'Rejeter' : 'Reject'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Request Details Modal */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedRequest && (i18n.language === 'fr' ? selectedRequest.title_fr : selectedRequest.title_en)}
              </DialogTitle>
            </DialogHeader>
            {selectedRequest && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  {getTypeIcon(selectedRequest.request_type)}
                  <span className="font-semibold">{getTypeLabel(selectedRequest.request_type)}</span>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">
                    {i18n.language === 'fr' ? 'Description:' : 'Description:'}
                  </h4>
                  <p className="text-gray-700">
                    {i18n.language === 'fr' ? selectedRequest.description_fr : selectedRequest.description_en}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold">{i18n.language === 'fr' ? 'Demandeur:' : 'Requester:'}</h4>
                    <p>{selectedRequest.user_name}</p>
                    <p className="text-sm text-gray-600">{selectedRequest.user_email}</p>
                  </div>
                  {selectedRequest.location && (
                    <div>
                      <h4 className="font-semibold">{i18n.language === 'fr' ? 'Localisation:' : 'Location:'}</h4>
                      <p>{selectedRequest.location}</p>
                    </div>
                  )}
                  {selectedRequest.budget && (
                    <div>
                      <h4 className="font-semibold">{i18n.language === 'fr' ? 'Budget estimé:' : 'Estimated Budget:'}</h4>
                      <p>{selectedRequest.budget} {selectedRequest.currency?.toUpperCase() || 'EUR'}</p>
                    </div>
                  )}
                  {selectedRequest.quantity && (
                    <div>
                      <h4 className="font-semibold">{i18n.language === 'fr' ? 'Quantité:' : 'Quantity:'}</h4>
                      <p>{selectedRequest.quantity} {selectedRequest.unit || ''}</p>
                    </div>
                  )}
                  {selectedRequest.deadline && (
                    <div>
                      <h4 className="font-semibold">{i18n.language === 'fr' ? 'Délai:' : 'Deadline:'}</h4>
                      <p>{selectedRequest.deadline}</p>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    variant="default"
                    onClick={() => {
                      handleApprove(selectedRequest.id);
                      setDialogOpen(false);
                    }}
                    className="flex-1 bg-[#635BFF] hover:bg-[#5248d9]"
                  >
                    <FaCheck className="mr-2" /> {i18n.language === 'fr' ? 'Approuver' : 'Approve'}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => {
                      setDialogOpen(false);
                      openRejectDialog(selectedRequest);
                    }}
                    className="flex-1"
                  >
                    <FaTimes className="mr-2" /> {i18n.language === 'fr' ? 'Rejeter' : 'Reject'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {i18n.language === 'fr' ? 'Rejeter la Demande' : 'Reject Request'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-600">
                {i18n.language === 'fr'
                  ? 'Veuillez fournir une raison pour le rejet (optionnel):'
                  : 'Please provide a reason for rejection (optional):'}
              </p>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder={i18n.language === 'fr' ? 'Raison du rejet...' : 'Reason for rejection...'}
                rows={4}
              />
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setRejectDialogOpen(false);
                    setRejectReason('');
                    setRequestToReject(null);
                  }}
                >
                  {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleReject}
                >
                  {i18n.language === 'fr' ? 'Confirmer le Rejet' : 'Confirm Rejection'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
